import React from "react";
import { initialize_auth_theme } from '@/uptk/core/theme/auth_theme';

/**
 * Authenticated session object held in context. The underlying value is an
 * untyped bag produced by the legacy auth bootstrap, so members are permissive,
 * but the commonly-invoked helpers (e.g. `getTheme`) are declared as callable so
 * consumers don't have to cast at every call site.
 */
export interface AuthState {
  getTheme?: () => any;
  setTheme?: (...args: any[]) => any;
  accountName?: any;
  // Auth helper methods exposed on the context value. Declared explicitly so
  // callers don't collapse to `unknown` via the index signature below.
  checkAccountRole?: (parent: any, role: string) => boolean;
  checkCurrentAccountRole?: (...args: any[]) => boolean;
  isPermissionGrantedToUser?: (...args: any[]) => boolean;
  isLoggedIn?: (...args: any[]) => boolean;
  checkAppFeature?: (...args: any[]) => boolean;
  getCurrentUserInfoFromBackend?: (...args: any[]) => any;
  getSignedAccountName?: (...args: any[]) => any;
  manageMsalLogin?: (...args: any[]) => any;
  [key: string]: unknown;
}

type ProvideAuthValue = [AuthState, React.Dispatch<React.SetStateAction<AuthState>>] | undefined;

const ProvideAuth = React.createContext(undefined as ProvideAuthValue);

const useAuthState = () => {

    const context = React.useContext(ProvideAuth);
    if (!context) throw new Error("useAuthState must be used in AuthProvider");

    return context;
}

interface AuthContextProviderProps {
    auth: Record<string, unknown>;
    children: React.ReactNode;
}

const AuthContextProvider = (props: AuthContextProviderProps) => {

    const [auth, setAuthState] = React.useState<AuthState>(() => initialize_auth_theme(props.auth) as AuthState);
    const setAuth = React.useCallback((nextAuth: React.SetStateAction<AuthState>) => {
        setAuthState((currentAuth: AuthState) => {
            const resolvedAuth =
                typeof nextAuth === 'function'
                    ? (nextAuth as (value: AuthState) => AuthState)(currentAuth)
                    : nextAuth;
            return initialize_auth_theme(resolvedAuth) as AuthState;
        });
    }, []);

    return (
        <ProvideAuth.Provider value={[auth, setAuth]}>
            {props.children}
        </ProvideAuth.Provider>
    );
};

export { AuthContextProvider, useAuthState, ProvideAuth};
export default ProvideAuth;
